'use es6';

import { CSS as DEFAULT_FILE_SOURCE_CSS, HTML, HTML_EMAIL } from 'DesignData/constants/DefaultFileSources';
import { EMAIL, PAGE, BLOG } from 'DesignData/enums/DesignAssetCategories';
import { byId as TemplateTypesById, byName as TemplateTypesByName } from 'ContentUtils/constants/TemplateTypes';
import getQuickFetchRequestPromise from 'DesignData/api/getQuickFetchRequestPromise';
import * as client from 'DesignData/api/TemplateBuffers';
import * as revisionsClient from 'DesignData/api/Revisions';
import { TEMPLATE_BUFFER_CLONE, TEMPLATE_BUFFER_DELETE, TEMPLATE_BUFFER_DISCARD, TEMPLATE_BUFFER_FETCH, TEMPLATE_BUFFER_FETCH_BY_PATH, TEMPLATE_BUFFER_MOVE_RENAME, TEMPLATE_BUFFER_PUBLISH, TEMPLATE_BUFFER_PUBLISH_REQUEST, TEMPLATE_BUFFER_REVERT, TEMPLATE_BUFFER_SAVE, TEMPLATE_BUFFER_SAVE_REQUEST, TEMPLATE_BUFFER_SAVE_BY_PATH, TEMPLATE_BUFFER_SAVE_BY_PATH_REQUEST, TEMPLATE_BUFFER_UPDATE_SOURCE, TEMPLATE_CREATE, TEMPLATE_CREATE_FROM_FILE, TEMPLATE_OPEN_BY_PATH, TEMPLATE_REVISION_FETCH } from 'DesignData/actions/ActionTypes';
import { addStagedFile } from 'DesignData/actions/Staging';
import * as templateBuffersSelectors from 'DesignData/selectors/templateBuffers';
import { NOT_MODIFIED } from 'DesignData/enums/HttpStatusCodes';
import { TEMPLATE } from 'DesignData/enums/DesignAssetTypes';
import { formatPathAsId, trimSlashesAndWhitespace } from 'DesignData/lib/text';
import { transformIdsForNonMysqlResource } from 'DesignData/lib/hollow/migrate';
export function getApiCallActions(actionType) {
  return {
    request: templateId => {
      return {
        type: actionType,
        templateId
      };
    },
    receive: (templateId, template, templateBuffer) => {
      return {
        type: actionType,
        templateId,
        template,
        templateBuffer
      };
    },
    error: (templateId, error) => {
      return {
        type: actionType,
        templateId,
        error
      };
    }
  };
}
export function getSavePublishRequestAction(actionType) {
  return (templateId, template) => {
    return {
      type: actionType,
      templateId,
      template
    };
  };
}
export function fetch(templateId) {
  const {
    request,
    receive,
    error
  } = getApiCallActions(TEMPLATE_BUFFER_FETCH);
  return (dispatch, getState) => {
    dispatch(request(templateId));
    const state = getState();
    const currentTemplate = templateBuffersSelectors.getTemplateBuffer(state, {
      templateId
    });

    const successHandler = tmpl => {
      const template = transformIdsForNonMysqlResource(TEMPLATE, templateId, tmpl, getState);
      dispatch(receive(template.id, template));
    };

    const errorHandler = _error => {
      if (_error.status && _error.status !== NOT_MODIFIED) {
        dispatch(error(templateId, _error));
      }
    };

    if (!currentTemplate) {
      const quickFetchPromise = getQuickFetchRequestPromise(`templateId-${templateId}`, () => client.fetch(templateId, {}));

      if (quickFetchPromise) {
        return quickFetchPromise.then(successHandler).catch(errorHandler);
      }
    } // This query allows us to 304 if we already have latest from server.


    const query = currentTemplate && currentTemplate.updated ? {
      last_updated: currentTemplate.updated
    } : {};
    return client.fetch(templateId, query).then(successHandler).catch(errorHandler);
  };
}
export function fetchByPath(templatePath) {
  const {
    request,
    receive,
    error
  } = getApiCallActions(TEMPLATE_BUFFER_FETCH_BY_PATH);
  return (dispatch, getState) => {
    templatePath = formatPathAsId(templatePath);
    dispatch(request(templatePath));

    const successHandler = tmpl => {
      const template = transformIdsForNonMysqlResource(TEMPLATE, templatePath, tmpl, getState);
      dispatch(receive(template.id, template));
    };

    const errorHandler = _error => {
      if (_error.status && _error.status !== NOT_MODIFIED) {
        dispatch(error(templatePath, _error));
      }
    };

    const state = getState();
    const currentTemplateBuffer = templateBuffersSelectors.getTemplateBuffer(state, {
      templatePath
    });

    if (!currentTemplateBuffer) {
      const quickFetchPromise = getQuickFetchRequestPromise(`templateId-${templatePath}`, () => client.fetch(templatePath, {}));

      if (quickFetchPromise) {
        return quickFetchPromise.then(successHandler).catch(errorHandler);
      }
    }

    return client.fetchByPath(trimSlashesAndWhitespace(templatePath)).then(successHandler).catch(errorHandler);
  };
}
export function fetchRevision(templateId, revisionId) {
  const {
    request,
    receive,
    error
  } = fetchRevision;
  return dispatch => {
    dispatch(request(templateId, revisionId));
    return revisionsClient.fetchTemplateRevision(templateId, revisionId).then(template => {
      dispatch(receive(templateId, revisionId, template));
    }).catch(_error => {
      dispatch(error(templateId, revisionId, _error));
    });
  };
}

fetchRevision.request = (templateId, revisionId) => {
  return {
    type: TEMPLATE_REVISION_FETCH,
    templateId,
    revisionId
  };
};

fetchRevision.receive = (templateId, revisionId, template) => {
  return {
    type: TEMPLATE_REVISION_FETCH,
    templateId,
    revisionId,
    template
  };
};

fetchRevision.error = (templateId, revisionId, error) => {
  return {
    type: TEMPLATE_REVISION_FETCH,
    templateId,
    revisionId,
    error
  };
};

export function create(templateDetails) {
  const {
    request,
    receive,
    error
  } = create;
  return dispatch => {
    dispatch(request());
    const {
      assetCategory,
      assetTemplateType: templateTypeId,
      fullpath: path,
      name
    } = templateDetails;
    const extension = name.split('.').pop().toLowerCase();

    const source = (() => {
      if (extension === 'css') return DEFAULT_FILE_SOURCE_CSS;
      if (extension !== 'html') return '';

      switch (templateTypeId) {
        case TemplateTypesByName.subscription_preferences.id:
        case TemplateTypesByName.unsubscribe_confirmation.id:
        case TemplateTypesByName.unsubscribe_simple.id:
          return '';

        default:
          return assetCategory === EMAIL ? HTML_EMAIL : HTML;
      }
    })();

    const {
      id: templateType,
      categoryId
    } = (() => {
      if (templateTypeId) return TemplateTypesById[templateTypeId];
      if (extension === 'css') return TemplateTypesByName.css;
      if (extension === 'js') return TemplateTypesByName.js;

      switch (assetCategory) {
        case EMAIL:
          return TemplateTypesByName.email;

        case PAGE:
          return TemplateTypesByName.landing_page;

        case BLOG:
          return TemplateTypesByName.blog_post;

        default:
          throw new TypeError(`DesignAssetCategories invalid for new template creation template type, received '${assetCategory}'`);
      }
    })();

    const newTemplateObject = {
      path,
      source: source.trim(),
      categoryId,
      templateType
    };
    client.create(newTemplateObject).then(template => {
      dispatch(receive(template));
    }).catch(__error => {
      dispatch(error(__error));
    });
  };
}

create.request = function request() {
  return {
    type: TEMPLATE_CREATE
  };
};

create.receive = function receive(template) {
  return {
    type: TEMPLATE_CREATE,
    template
  };
};

create.error = function error(__error) {
  return {
    type: TEMPLATE_CREATE,
    error: __error
  };
};
/**
 * Finder sync middleware action
 */


export function createFromFile(template) {
  return {
    type: TEMPLATE_CREATE_FROM_FILE,
    templateId: template.id,
    template
  };
}
export function save(templateId) {
  const request = getSavePublishRequestAction(TEMPLATE_BUFFER_SAVE_REQUEST);
  const {
    receive,
    error
  } = getApiCallActions(TEMPLATE_BUFFER_SAVE);
  return (dispatch, getState) => {
    const state = getState();
    const {
      isSaving,
      isPublishing
    } = templateBuffersSelectors.getRequestStatus(state);

    if (isSaving || isPublishing) {
      return null;
    }

    const template = templateBuffersSelectors.getTemplateBuffer(state, {
      templateId
    });
    dispatch(request(templateId, template));
    return client.save(templateId, template).then(savedTemplate => {
      dispatch(receive(templateId, savedTemplate));
    }).catch(_error => {
      dispatch(error(templateId, _error));
    });
  };
}
export function saveByPath(templateId) {
  const request = getSavePublishRequestAction(TEMPLATE_BUFFER_SAVE_BY_PATH_REQUEST);
  const {
    receive,
    error
  } = getApiCallActions(TEMPLATE_BUFFER_SAVE_BY_PATH);
  return (dispatch, getState) => {
    const state = getState();
    const {
      isSaving,
      isPublishing
    } = templateBuffersSelectors.getRequestStatus(state);

    if (isSaving || isPublishing) {
      return null;
    }

    const template = templateBuffersSelectors.getTemplateBuffer(state, {
      templateId
    });
    dispatch(request(templateId, template));
    return client.saveByPath(template.path, template).then(savedTemplate => {
      dispatch(receive(templateId, savedTemplate, template));
      dispatch(addStagedFile(template, savedTemplate));
    }).catch(_error => {
      dispatch(error(templateId, _error));
    });
  };
}
export function clone(templateIdOrPath, template) {
  const {
    receive,
    error
  } = getApiCallActions(TEMPLATE_BUFFER_CLONE);
  return dispatch => {
    const cloneMethod = isNaN(templateIdOrPath) ? client.cloneByPath : client.clone;
    return cloneMethod(templateIdOrPath, template).then(clonedTemplate => {
      dispatch(receive(clonedTemplate.id, clonedTemplate));
    }).catch(_error => {
      dispatch(error(templateIdOrPath, _error));
    });
  };
}
export function deleteTemplate(templateId) {
  const {
    receive,
    error
  } = getApiCallActions(TEMPLATE_BUFFER_DELETE);
  return dispatch => {
    return client.deleteTemplate(templateId).then(() => {
      dispatch(receive(templateId));
    }).catch(_error => {
      dispatch(error(templateId, _error));
    });
  };
}
/**
 * Finder sync middleware action
 * @param {(number, number[])} a single template id or array of template ids to delete.
 */

export function deleteTemplates(templateIds) {
  return {
    type: TEMPLATE_BUFFER_DELETE,
    templateId: templateIds
  };
}
export function publish(templateId, template) {
  const request = getSavePublishRequestAction(TEMPLATE_BUFFER_PUBLISH_REQUEST);
  const {
    receive,
    error
  } = getApiCallActions(TEMPLATE_BUFFER_PUBLISH);
  return dispatch => {
    dispatch(request(templateId, template));
    return client.publish(templateId, template).then(publishedTemplate => {
      dispatch(receive(templateId, publishedTemplate));
    }).catch(_error => {
      dispatch(error(templateId, _error));
    });
  };
}
export function moveRename(templateId, template) {
  const {
    request,
    receive,
    error
  } = getApiCallActions(TEMPLATE_BUFFER_MOVE_RENAME);
  return dispatch => {
    dispatch(request(templateId));
    return client.moveRename(templateId, template).then(movedTemplate => {
      dispatch(receive(templateId, movedTemplate));
    }).catch(_error => {
      dispatch(error(templateId, _error));
    });
  };
}
/**
 * Finder sync middleware action
 */

export function moveOrRenameFromFile(templateId, template) {
  return {
    type: TEMPLATE_BUFFER_MOVE_RENAME,
    templateId,
    template
  };
}
export function discardBuffer(templateId) {
  const {
    receive,
    error
  } = getApiCallActions(TEMPLATE_BUFFER_DISCARD);
  return dispatch => {
    return client.discardBuffer(templateId).then(() => client.fetch(templateId)).then(template => {
      dispatch(receive(templateId, template));
    }).catch(_error => {
      dispatch(error(templateId, _error));
    });
  };
}
export function revert(revision) {
  const templateId = revision.object.id;
  const {
    receive,
    error
  } = getApiCallActions(TEMPLATE_BUFFER_REVERT);
  return dispatch => {
    return revisionsClient.revertTemplate(templateId, revision.id, true).then(template => {
      dispatch(receive(templateId, template));
    }).catch(_error => {
      dispatch(error(templateId, _error));
    });
  };
}
export function updateSource(templateId, source) {
  return {
    type: TEMPLATE_BUFFER_UPDATE_SOURCE,
    templateId,
    source
  };
}
export function openByPath(path) {
  const {
    request,
    receive,
    error
  } = openByPath;
  return dispatch => {
    dispatch(request(path));
    return client.fetchByPath(path).then(template => {
      if (template) {
        dispatch(receive(template));
      } else {
        const _error = new ReferenceError(`Unable to find template with path '${path}'`);

        dispatch(error(_error, path));
      }
    }).catch(_error => {
      dispatch(error(_error, path));
    });
  };
}

openByPath.request = function openByPathRequest(path) {
  return {
    type: TEMPLATE_OPEN_BY_PATH,
    path
  };
};

openByPath.receive = function openByPathReceive(template) {
  return {
    type: TEMPLATE_OPEN_BY_PATH,
    template
  };
};

openByPath.error = function openByPathError(error, path) {
  return {
    type: TEMPLATE_OPEN_BY_PATH,
    error,
    path
  };
};